import { useState, useEffect } from "react";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import Button from "../UserInput/Buttons/Button";
import appApi from "./../../api/index";
// eslint-disable-next-line no-unused-vars
import { ModalProps } from "@mui/material/Modal"; // Only used for JSDoc comment

/**
 * @typedef {object} Question
 * @property {number} id
 * @property {number} company_id
 * @property {string} type
 * @property {string} question
 * @property {string | null} secondary_question
 * @property {string} required
 * @property {string[] | null} code_keys
 * @property {string | null} comment
 * @property {unknown} details
 * @property {boolean | null} is_random
 * @property {number} order_number
 * @property {string} created_at
 * @property {string} updated_at
 */

/**
 * Dialog that prompts for confirmation before deleting a question.
 * @param {object} props
 * @param {string} props.title Title for the dialog.
 * @param {React.ReactNode} props.content Content for the dialog.
 * @param {Question} props.question The question to delete.
 * @param {boolean} props.show `true` to show the dialog and `false` to hide it.
 * @param {ModalProps["onClose"]} props.onClose Called when a request to close the dialog is made.
 * @param {({ question: Question, success: boolean, message: string }) => void} props.onDeleteCallback Called when the delete button is clicked.
 */
function DeleteQuestionDialog({ title, content, question, show, onClose, onDeleteCallback }) {
    const [open, setOpen] = useState(show || false);

    const handleClose = () => {
        onClose();
        setOpen(false);
    };

    const onDeleteQuestion = async () => {
        const response = await appApi.delete(`/question/${question.id}`);

        handleClose();

        onDeleteCallback({
            deletedQuestion: question,
            success: response.data.success,
            message: response.data.message,
        });
    };

    useEffect(() => {
        setOpen(show);
    }, [show]);

    return (
        <>
            <Dialog
                open={open}
                onClose={handleClose}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
            >
                <DialogTitle id="alert-dialog-title">{title || "Success"}</DialogTitle>
                <DialogContent>
                    <DialogContentText id="alert-dialog-description">{content}</DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button size="xs" colorTheme="danger" handleOnClick={onDeleteQuestion}>
                        Delete
                    </Button>
                    <Button size="xs" colorTheme="secondary" handleOnClick={handleClose} autoFocus>
                        Cancel
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}

export default DeleteQuestionDialog;
